<script setup>
import { computed, reactive, ref } from 'vue';
import { useCookieStore } from '../store/cookie';
import { useMainStore } from '../store/index';
import { useCookies } from '@vueuse/integrations/useCookies';

const emit = defineEmits(['close']);

const cookieStore = useCookieStore();
const store = useMainStore();

const cookies = ref([
  {
    type: 'necessary',
    alwaysOn: true,
    title: 'cookiesNecessaryCookies',
    description: 'cookiesNecessaryCookiesText',
    showDescription: false,
  },
  {
    type: 'performance',
    alwaysOn: false,
    value: cookieStore.cookieConsent?.performance || false,
    title: 'cookiesPerformanceCookies',
    description: 'cookiesPerformanceCookiesText',
    showDescription: false,
  },
  {
    type: 'functional',
    alwaysOn: false,
    value: cookieStore.cookieConsent?.functional || false,
    title: 'cookiesFunctionalCookies',
    description: 'cookiesFunctionalCookiesText',
    showDescription: false,
  },
  {
    type: 'targeting',
    alwaysOn: false,
    value: cookieStore.cookieConsent?.targeting || false,
    title: 'cookiesTargetingCookies',
    description: 'cookiesTargetingCookiesText',
    showDescription: false,
  },
  {
    type: 'socialMedia',
    alwaysOn: false,
    value: cookieStore.cookieConsent?.socialMedia || false,
    title: 'cookiesSocialMediaCookies',
    description: 'cookiesSocialMediaCookiesText',
    showDescription: false,
  },
]);

const cookieName = 'DigitalSales.CookieConsent';
const optionCookie = { path: '/', sameSite: 'Strict', secure: true, maxAge :1296000 };

const cookieUtil = useCookies([cookieName], { doNotParse: false });

const saveSelections = () => {
  let cookieValue = {};
  cookies.value?.forEach((c)=>{
    cookieValue[c.type]=c.alwaysOn ||  c.value;
  });
  cookieUtil.set(cookieName, JSON.stringify(cookieValue), optionCookie);
  cookieStore.saveConsent(cookieValue);
  emit('close');
};

const closeForm = () => {
  emit('close');
};
</script>
<template>
  <div
    id="cookieMessageModal"
    class="sdds-modal-backdrop show"
    prevent
  >
    <div class="sdds-modal sdds-modal-sm">
      <div class="sdds-modal-header">
        <div class="sdds-modal-headline">
          <h5>{{ $i18n('cookiePrivacySettings') }}</h5>
        </div>
      </div>
      <div class="sdds-modal-body modal-body-padding">
        <!--         <div class="modal-description">
          <p>
            {{ $i18n('cookiesConsentText') }}
          </p>
        </div> -->
        <ul>
          <li
            v-for="cookie in cookies"
            :key="cookie.type"
            class="sdds-accordion-item"
            :class="{ 'expanded': cookie.showDescription }"
          >
            <div
              class="sdds-accordion-header-prefix"
              @click="cookie.showDescription = !cookie.showDescription"
            >
              <div
                class="sdds-accordion-title"
              >
                {{ $i18n(cookie.title) }}
              </div>
              <div
                class="sdds-accordion-icon"
              >
                <svg
                  width="12"
                  height="7"
                  viewBox="0 0 12 7"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L6 6L11 1"
                    stroke="currentColor"
                    stroke-width="1.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div
                class="cookie-toggle cookie-setting"
                tabindex="1"
              >
                <label v-if="cookie.alwaysOn">{{ $i18n('cookiesAlwaysOn') }}</label>
                <template v-else>
                  <input
                    v-model="cookie.value"
                    type="checkbox"
                    class="cookie-toggle-input"
                    @click.stop
                  ><span
                    class="cookie-toggle-switch"
                  /><label class="cookie-toggle-label">{{ cookie.value ? $i18n('cookieOn') : $i18n('cookieOff') }}
                  </label>
                </template>
              </div>
            </div>
            <div class="sdds-accordion-panel">
              {{ $i18n(cookie.description) }}
            </div>
          </li>
        </ul>
        <div class="sdds-modal-footer">
          <p>
            {{ $i18n('cookiesReadMore') }}
            <a
              class="sope-link"
              :href="store.cookiesPrivacyLinkForActiveMarket"
              target="_blank"
              data-dd-action-name="cookies privacy link"
            > {{ $i18n('cookiesHere') }}</a>
          </p>
        </div>
      </div>
      <div class="sdds-modal-actions">
        <button
          class="sdds-btn sdds-btn-secondary sdds-btn-md"
          data-dd-action-name="cookies modal cancel"
          @click="closeForm"
        >
          {{ $i18n('cookiesBtnCancel') }}
        </button><button
          class="sdds-btn sdds-btn-primary sdds-btn-md"
          data-dd-action-name="cookies modal save selections"
          @click="saveSelections"
        >
          {{ $i18n('cookiesBtnSaveSelections') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sdds-modal-backdrop {
  z-index: 2999999999;
}

.sdds-modal {
  z-index: 4;
  margin: auto;
  position: relative;
  border-radius: 1rem;
  padding: 16px;
}

.sdds-modal .sdds-modal-body {
  border-top: 1px solid var(--sdds-grey-100);
  border-bottom: 1px solid var(--sdds-grey-100);
  height: 50vh;
  overflow-y: auto;
}

.sdds-modal-body>* {
  font-size: 14px;
}

.sdds-modal .sdds-modal-headline h3 {
  font-size: 20px;
}

.sdds-modal .modal-description {
  display: block;
  padding: 0 16px;
}

.sdds-modal .modal-description>p {
  line-height: 25px;
}


.sdds-modal ul {
  padding: 0;
  margin: 0;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-direction: column;
  flex-direction: column;
}

.sdds-modal li {
  margin: 0;
  list-style-type: none;
  font-family: 'Scania Sans Semi Condensed', Arial, Helvetica, sans-serif;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-direction: column;
  flex-direction: column;
}

.sdds-modal .sdds-modal.sdds-modal-sm {
  max-width: 600px;
  z-index: 3999999999;
}

.sdds-modal ::-webkit-scrollbar-thumb {
  background-color: var(--sdds-grey-600);
}

.sdds-modal ::-webkit-scrollbar {
  width: 6px;
  background: transparent;
}

.sdds-modal ::-webkit-scrollbar-button {
  height: 0;
  width: 0;
}

.sdds-modal .sdds-accordion-item {
  border-bottom: 1px solid #edeff3;
}


.sdds-modal .sdds-modal-actions {
  display: -ms-flexbox;
  display: flex;
  -ms-flex-pack: end;
  justify-content: flex-end;
  margin: 0;
  padding-top: 12px;
}

.sdds-modal .sdds-modal-header {
  padding: 16px;
}

.sdds-modal .sdds-modal-footer {
  padding: 0 16px;
}

.sdds-modal .sdds-modal-footer p {
  font-size: 14px;
}

.sdds-accordion-item .sdds-accordion-panel {
  padding-right: var(--sdds-spacing-layout-16);
}

.cookie-toggle {
  position: relative;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-align: center;
  align-items: center;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
}

.cookie-toggle.cookie-setting {
  -ms-flex-order: 10;
  order: 10;
  min-width: 80px;
}

.cookie-toggle,
.cookie-toggle * {
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

.cookie-toggle .cookie-toggle-headline {
  font-family: 'Scania Sans Semi Condensed', 'Scania Sans Condensed', Arial,
    Helvetica, sans-serif;
  font-size: 3.5rem;
  line-height: 4rem;
  letter-spacing: -0.01em;
  color: var(--sdds-toggle-headline);
  -ms-flex: 1 100%;
  flex: 1 100%;
  margin-bottom: 3rem;
}

.cookie-toggle .cookie-toggle-input {
  margin: 0;
  padding: 0;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  opacity: 0;
  z-index: 1;
  cursor: pointer;
}

.cookie-toggle .cookie-toggle-switch {
  width: var(--sdds-toggle-slider-width);
  height: var(--sdds-toggle-slider-height);
  position: relative;
  display: block;
}

.cookie-toggle .cookie-toggle-switch:after,
.cookie-toggle .cookie-toggle-switch:before {
  content: '';
  position: absolute;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  -webkit-transition: all 0.5s cubic-bezier(0.075, 0.82, 0.165, 1);
  transition: all 0.5s cubic-bezier(0.075, 0.82, 0.165, 1);
}

.cookie-toggle .cookie-toggle-switch:before {
  width: var(--sdds-toggle-slider-width);
  height: var(--sdds-toggle-slider-height);
  border-radius: var(--sdds-toggle-slider-border-radius);
  background-color: var(--sdds-toggle-off-slider);
  left: 0;
}

.cookie-toggle .cookie-toggle-switch:after {
  width: 16px;
  height: 16px;
  background-color: var(--sdds-toggle-switch);
  border-radius: 50%;
  left: 4px;
  top: 4px;
}

.cookie-toggle .cookie-toggle-label {
  font-family: 'Scania Sans Semi Condensed', 'Scania Sans Condensed', Arial,
    Helvetica, sans-serif;
  font-size: 4rem;
  line-height: 6rem;
  letter-spacing: -0.01em;
  color: var(--sdds-toggle-label-colour);
  padding-left: var(--sdds-spacing-element-8);
}

.cookie-toggle .cookie-toggle-input:checked+.cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-on-slider);
}

.cookie-toggle .cookie-toggle-input:checked+.cookie-toggle-switch:after {
  left: calc(var(--sdds-toggle-slider-width) - 20px);
}

.cookie-toggle .cookie-toggle-input:focus+.cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-off-slider-focus);
  border: 1px solid var(--sdds-toggle-off-border-focus);
}

.cookie-toggle .cookie-toggle-input:focus:checked+.cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-on-slider-focus);
  border: 1px solid var(--sdds-toggle-on-border-focus);
}

.cookie-toggle .cookie-toggle-input:active+.cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-off-slider-pressed);
  border: 1px solid var(--sdds-toggle-off-slider-pressed);
}

.cookie-toggle .cookie-toggle-input:active:checked+.cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-on-slider-pressed);
  border: 1px solid var(--sdds-toggle-on-slider-pressed);
}

.cookie-toggle.cookie-toggle-sm .cookie-toggle-switch,
.cookie-toggle.cookie-toggle-sm .cookie-toggle-switch:before {
  width: var(--sdds-toggle-slider-sm-width);
  height: var(--sdds-toggle-slider-sm-height);
}

.cookie-toggle.cookie-toggle-sm .cookie-toggle-switch:after {
  width: 8px;
  height: 8px;
}

.cookie-toggle.cookie-toggle-sm .cookie-toggle-label {
  font-family: 'Scania Sans Semi Condensed', 'Scania Sans Condensed', Arial,
    Helvetica, sans-serif;
  font-size: 3.5rem;
  line-height: 4rem;
  letter-spacing: -0.01em;
}

.cookie-toggle.cookie-toggle-sm .cookie-toggle-input:checked+.cookie-toggle-switch:after {
  left: calc(var(--sdds-toggle-slider-sm-width) - 12px);
}

.cookie-toggle:hover .cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-off-slider-hover);
}

.cookie-toggle:hover .cookie-toggle-input:checked+.cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-on-slider-hover);
}

.cookie-toggle:focus {
  outline: none;
}

.cookie-toggle:active .cookie-toggle-input+.cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-off-slider-pressed);
  border: 1px solid var(--sdds-toggle-off-slider-pressed);
}

.cookie-toggle:active .cookie-toggle-input:checked+.cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-on-slider-pressed);
  border: 1px solid var(--sdds-toggle-on-slider-pressed);
}

.cookie-toggle.disabled {
  cursor: not-allowed;
}

.cookie-toggle.disabled .cookie-toggle-input {
  pointer-events: none;
}

.cookie-toggle.disabled .cookie-toggle-input+.cookie-toggle-switch:before,
.cookie-toggle.disabled .cookie-toggle-input:checked+.cookie-toggle-switch:before,
.cookie-toggle.disabled .cookie-toggle-input:checked:focus+.cookie-toggle-switch:before,
.cookie-toggle.disabled .cookie-toggle-input:focus+.cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-slider-disabled);
  border: 0;
}

.cookie-toggle.disabled .cookie-toggle-headline {
  color: var(--sdds-toggle-headline-disabled);
}

.cookie-toggle.disabled .cookie-toggle-label {
  color: var(--sdds-toggle-label-colour-disabled);
  pointer-events: none;
}

.cookie-toggle.disabled .cookie-toggle-switch:before {
  background-color: var(--sdds-toggle-slider-disabled);
}

.cookie-toggle.disabled .cookie-toggle-switch:after {
  background-color: var(--sdds-toggle-switch-disabled);
}

@media screen and (max-width: 768px) {
  .sdds-modal .sdds-btn {
    margin-bottom: 5px;
  }

  .sdds-modal .sdds-modal-actions {
    -ms-flex-direction: column-reverse;
    flex-direction: column-reverse;
  }

  .sdds-modal .modal-description {
    display: none;
  }

}

@media screen and (max-width: 450px) {
  .sdds-accordion-title{
   /*  max-width: 50vw; */
  }
}
</style>
